import React, { useEffect, useState, useCallback } from "react";
import { Layout } from "antd";
import { useSelector } from "react-redux";
import { baseUrl } from "../../constants/api";
import { API_ENDPOINTS } from "../../constants/endpoints";
import profilecardtop from "../../assets/images/userprofiletop.svg";
import showToastMessage from "../../utils/showToastMessage";
import { RootState } from "../../store";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ShimmerBoxProps {
  height: string;
  width: string;
  className?: string;
}

const ShimmerBox: React.FC<ShimmerBoxProps> = ({ height, width, className }) => (
  <div className={`shimmer-box ${className || ''}`} style={{ height, width }}></div>
);

interface UserProfile {
  name: string;
  designation: string;
  reference_id: string;
  phone: string;
  role?: string;
  cli_id?: string | null;
  cli_name?: string | null;
}

interface CliOption {
  _id: string;
  name: string;
}

export default function ProfileCard() {
  const [userProfileData, setUserProfileData] = useState<UserProfile | null>(null);
  const [cliOptions, setCliOptions] = useState<CliOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedPhone, setEditedPhone] = useState("");
  const [editedCliId, setEditedCliId] = useState("");
  const [updating, setUpdating] = useState(false);

  const token = useSelector((state: RootState) => state.account.token);

  const fetchUserDetails = useCallback(async () => {
    const apiUrl = `${baseUrl}${API_ENDPOINTS.GET_USER_DETAILS}`;

    if (!token) {
      showToastMessage("Token is missing. Please log in again.", "error");
      return;
    }

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfileData(data.data);
        setEditedName(data.data.name);
        setEditedPhone(data.data.phone);
        setEditedCliId(data.data.cli_id || "");
      } else {
        const errorText = await response.text();
        showToastMessage(`Failed to fetch user details: ${errorText}`, "error");
      }
    } catch (error) {
      showToastMessage("An error occurred while fetching user details.", "error");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  useEffect(() => {
    const fetchCliOptions = async () => {
      if (!token) {
        return;
      }

      try {
        const response = await fetch(`${baseUrl}${API_ENDPOINTS.GET_CLI_OPTIONS}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        setCliOptions(data.data || []);
      } catch (error) {
        console.error("Failed to fetch CLI options", error);
      }
    };

    fetchCliOptions();
  }, [token]);

  const handleUpdateProfile = async () => {
    if (!editedName.trim() || !editedPhone.trim()) {
      showToastMessage("Name and Phone number are required.", "error");
      return;
    }

    setUpdating(true);
    const apiUrl = `${baseUrl}${API_ENDPOINTS.UPDATE_USER_PROFILE}`;

    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editedName,
          phone: editedPhone,
          cli_id: userProfileData?.role?.toUpperCase() === "USER" ? editedCliId || null : undefined,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfileData(data.data);
        setEditedCliId(data.data.cli_id || "");
        setIsEditing(false);
        showToastMessage("Profile updated successfully!", "success");
      } else {
        const data = await response.json();
        showToastMessage(data.message || "Failed to update profile", "error");
      }
    } catch (error) {
      showToastMessage("An error occurred while updating profile.", "error");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    if (userProfileData) {
      setEditedName(userProfileData.name);
      setEditedPhone(userProfileData.phone);
      setEditedCliId(userProfileData.cli_id || "");
    }
    setIsEditing(false);
  };

  const getInitials = (name: string) => {
    if (!name) return "";
    const nameParts = name.split(" ");
    return nameParts.map((part) => part[0]).join("").toUpperCase();
  };

  const selectedCli = cliOptions.find((cli) => cli._id === (isEditing ? editedCliId : userProfileData?.cli_id));
  const cliDisplayValue = selectedCli
    ? selectedCli.name
    : userProfileData?.cli_name || "No CLI selected";

  return (
    <Layout>
      <div className="profile-card-container">
        <div className="profile-card w-100">
          <div className="profile-card-top">
            <img className="profile-card-head-img" src={profilecardtop} alt="Profile" />
          </div>
          <div className="spacer">
            {loading ? (
              <>
                <div className="profile-card-header">
                  <ShimmerBox height="80px" width="80px" className="avatar-placeholder" />
                  <div className="profile-name">
                    <ShimmerBox height="24px" width="150px" />
                    <ShimmerBox height="18px" width="100px" />
                  </div>
                </div>
                <div className="profile-details">
                  <div className="detail-row">
                    <ShimmerBox height="60px" width="48%" />
                    <ShimmerBox height="60px" width="48%" />
                  </div>
                  <div className="detail-row">
                    <ShimmerBox height="60px" width="48%" />
                    <ShimmerBox height="60px" width="48%" />
                  </div>
                </div>
              </>
            ) : userProfileData ? (
              <>
                {(() => {
                  const isNormalUser = userProfileData.role?.toUpperCase() === "USER";
                  return (
                    <>
                <div className="profile-card-header">
                  <div className="avatar-placeholder">{getInitials(userProfileData.name)}</div>
                  <div className="profile-name">
                    <h2 style={{ margin: "0px", padding: "0px" }}>{userProfileData.name}</h2>
                    <p>{userProfileData.designation}</p>
                  </div>
                  {!isEditing && (
                    <button
                      className="edit-profile-btn"
                      onClick={() => setIsEditing(true)}
                    >
                      Edit Profile
                    </button>
                  )}
                </div>
                <div className="profile-details">
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Full Name</label>
                      <input
                        type="text"
                        value={isEditing ? editedName : userProfileData.name}
                        onChange={(e) => setEditedName(e.target.value)}
                        disabled={!isEditing}
                        className={isEditing ? "editing-input" : ""}
                      />
                    </div>
                    <div className="detail-item">
                      <label>Designation</label>
                      <input type="text" value={userProfileData.designation} disabled />
                    </div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>HRMS</label>
                      <input type="text" value={userProfileData.reference_id} disabled />
                    </div>
                    <div className="detail-item">
                      <label>Phone No</label>
                      <input
                        type="text"
                        value={isEditing ? editedPhone : userProfileData.phone}
                        onChange={(e) => setEditedPhone(e.target.value)}
                        disabled={!isEditing}
                        className={isEditing ? "editing-input" : ""}
                      />
                    </div>
                  </div>
                  {isNormalUser && (
                    <div className="detail-row">
                      <div className="detail-item full-width">
                        <label>CLI</label>
                        {isEditing ? (
                          <Select value={editedCliId || undefined} onValueChange={setEditedCliId}>
                            <SelectTrigger className="profile-cli-select-trigger">
                              <SelectValue placeholder="Select CLI" />
                            </SelectTrigger>
                            <SelectContent>
                              {cliOptions.map((cli) => (
                                <SelectItem key={cli._id} value={cli._id}>
                                  {cli.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <input type="text" value={cliDisplayValue} disabled />
                        )}
                      </div>
                    </div>
                  )}
                  {isEditing && (
                    <div className="edit-actions">
                      <button
                        className="save-btn"
                        onClick={handleUpdateProfile}
                        disabled={updating}
                      >
                        {updating ? "Saving..." : "Save Changes"}
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={handleCancelEdit}
                        disabled={updating}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                    </>
                  );
                })()}
              </>
            ) : (
              <div className="loading-text">Failed to load profile data.</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
